import { uiTokens } from "../../../../utils/uiTokens";
import { getUserInitials } from "../../../../utils/userInitials";
import { WORKER_ROLE_BUCKETS } from "./heatmapHelpers";

const SIZE = 64;
const BORDER = 4;

const BORDER_COLOR_BY_BUCKET = {
  [WORKER_ROLE_BUCKETS.supervisor]: uiTokens.color.semantic.primary.default,
  [WORKER_ROLE_BUCKETS.organizer]: uiTokens.color.primitive.cyan[800],
  [WORKER_ROLE_BUCKETS.offline]: uiTokens.color.semantic.foreground.subtle,
};

/** Builds a circular avatar marker (photo or initials) with a role-colored
 * border, as a data-URI so it can be used as a Google Maps marker icon
 * (which only accepts image URLs, not JSX/DOM). */
export const buildMarkerIconUrl = ({ imageUrl, name, bucket }) => {
  const borderColor = BORDER_COLOR_BY_BUCKET[bucket];
  const ringRadius = SIZE / 2 - BORDER / 2;
  const contentRadius = ringRadius - BORDER / 2;
  const center = SIZE / 2;

  const content = imageUrl
    ? `<clipPath id="clip"><circle cx="${center}" cy="${center}" r="${contentRadius}"/></clipPath>
       <image href="${imageUrl}" x="${BORDER}" y="${BORDER}" width="${SIZE - BORDER * 2}" height="${SIZE - BORDER * 2}" clip-path="url(#clip)" preserveAspectRatio="xMidYMid slice"/>`
    : `<circle cx="${center}" cy="${center}" r="${contentRadius}" fill="${uiTokens.color.primitive.neutral[200]}"/>
       <text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="20" font-weight="600" fill="${uiTokens.color.primitive.neutral[700]}">${getUserInitials(name)}</text>`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
    <defs>
      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="6.4" stdDeviation="4.8" flood-color="#000000" flood-opacity="0.15"/>
      </filter>
    </defs>
    <g filter="url(#shadow)">
      <circle cx="${center}" cy="${center}" r="${ringRadius}" fill="${uiTokens.color.primitive.neutral[0]}" stroke="${borderColor}" stroke-width="${BORDER}"/>
      ${content}
    </g>
  </svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const MARKER_ICON_SIZE = SIZE;

const CLUSTER_SIZE = 64;
const CLUSTER_BORDER = 4;

/** Builds a numbered circle marker (data-URI) representing a group of
 * staff clustered together at the current zoom level. */
export const buildClusterIconUrl = (count) => {
  const center = CLUSTER_SIZE / 2;
  const radius = CLUSTER_SIZE / 2 - CLUSTER_BORDER / 2;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${CLUSTER_SIZE}" height="${CLUSTER_SIZE}" viewBox="0 0 ${CLUSTER_SIZE} ${CLUSTER_SIZE}">
    <defs>
      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="4.8" stdDeviation="4.8" flood-color="#000000" flood-opacity="0.2"/>
      </filter>
    </defs>
    <g filter="url(#shadow)">
      <circle cx="${center}" cy="${center}" r="${radius}" fill="${uiTokens.color.semantic.purple.default}" stroke="${uiTokens.color.primitive.neutral[0]}" stroke-width="${CLUSTER_BORDER}"/>
      <text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="${uiTokens.color.primitive.neutral[0]}">${count}</text>
    </g>
  </svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const CLUSTER_ICON_SIZE = CLUSTER_SIZE;
